import { forwardRef, useMemo } from 'react';
import type { VideoHTMLAttributes } from 'react';

import { mergeRefs } from '@opendoor/atoms/utils';
import { useInView } from 'react-intersection-observer';

const LOAD_ROOT_MARGIN = '600px 0px';

type DeferredAutoplayVideoProps = Omit<
  VideoHTMLAttributes<HTMLVideoElement>,
  'autoPlay' | 'children' | 'loop' | 'muted' | 'playsInline' | 'preload' | 'src'
> & {
  eager?: boolean;
  src: string;
};

export const DeferredAutoplayVideo = forwardRef<HTMLVideoElement, DeferredAutoplayVideoProps>(
  function DeferredAutoplayVideo({ eager = false, src, ...props }, forwardedRef) {
    const { ref: inViewRef, inView } = useInView({
      rootMargin: LOAD_ROOT_MARGIN,
      triggerOnce: true,
      initialInView: eager,
      skip: eager,
    });
    const ref = useMemo(() => mergeRefs(inViewRef, forwardedRef), [forwardedRef, inViewRef]);
    const shouldLoad = eager || inView;
    let preload: 'auto' | 'metadata' | 'none' = 'none';
    if (shouldLoad) {
      preload = eager ? 'auto' : 'metadata';
    }

    return (
      <video
        {...props}
        ref={ref}
        src={shouldLoad ? src : undefined}
        preload={preload}
        autoPlay
        muted
        loop
        playsInline
      />
    );
  },
);
